import {captureMessage} from '@sentry/browser';
import {DeleteDialog, showNotification, useDialog} from 'platform/components';
import {
  InternalLightboxControls,
  Lightbox,
  LightboxVideo,
  useLightboxActionsFactory,
} from 'platform/lightbox';

import {useMemo} from 'react';

import {isNil} from 'ramda';

import {AuditDataVideoAssetsFilesBody} from '@dms/api/core';
import {i18n} from '@dms/i18n';
import {downloadFile, isVideoPlayable} from '@dms/shared';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

interface AuditVideoLightboxProps extends TestIdProps {
  videoAssets: AuditDataVideoAssetsFilesBody[];
  controls: InternalLightboxControls;
  onDeleteVideo: (video: LightboxVideo) => void;
}

export function AuditVideoLightbox(props: AuditVideoLightboxProps) {
  const actionsFactory = useLightboxActionsFactory();
  const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog, {storage: videoToDelete}] =
    useDialog<LightboxVideo>();

  const lightboxVideoAssets: LightboxVideo[] | Nullish = useMemo(
    () =>
      actionsFactory.addShareOnlyActions(props.videoAssets.filter(isVideoPlayable), (asset) => [
        {
          id: 'downloadBtn',
          icon: 'file/download',
          onClick: () => {
            if (isNil(asset.originUrl)) {
              showNotification.error(i18n.t('entity.inspection.errors.downloadFailed'));
              captureMessage('Error downloading video asset – originUrl is not defined', (scope) =>
                scope.setExtras({asset})
              );
            } else {
              downloadFile(asset.originUrl);
            }
          },
        },
        {
          id: 'deleteBtn',
          icon: 'action/delete',
          onClick: () => openDeleteDialog(asset),
        },
      ]),
    [actionsFactory, props.videoAssets, openDeleteDialog]
  );

  return (
    <>
      <Lightbox
        data-testid={suffixTestId('audit-uploadVideosGallery', props)}
        controls={props.controls}
        videos={lightboxVideoAssets}
      />
      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={() => props.onDeleteVideo(videoToDelete!)}
        isInLightbox
      />
    </>
  );
}
